import { useEffect, useMemo, useState } from 'react';
import loadable from '@loadable/component';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import toLower from 'ramda/src/toLower';
import Avatar from '@components/@core/Avatar';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Icon from '@components/@core/Icon';
import EditDropdown from '@components/EditDropdown';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import ManageActions from '~/actions/manage';
import TrainingActivity from './TrainingActivity';
import PerformanceReviews from './PerformanceReviews';
import TasksProjects from './TasksProjects';
import NewHireOrientations from './NewHireOrientations';
import CareerManagement from './CareerManagement';
import EmployeeRecords from './EmployeeRecords';

const PdfIcon = loadable(() => import('./ManagerDashboardPdf'), { ssr: false });

const ManagerDashboard = () => {
    const dispatch = useDispatch();

    const cId = useSelector((state) => state.app.company_info.id);
    const companies = useSelector((state) => state.app.companies);
    const selectedCompany = useSelector((state) => state.app.selectedCompany);
    const employees = useSelector((state) => state.app.users);
    const reports = useSelector((state) => state.manage.managerDashboard);
    const [showPrint, setShowPrint] = useState(false);

    const [companyId, setCompanyId] = useState(selectedCompany[0]);
    const [dateStart, setDateStart] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [dateEnd, setDateEnd] = useState(moment().endOf('month').format('YYYY-MM-DD'));

    const handleDate = (e) => {
        const ds = moment(e.start).format('YYYY-MM-DD');
        const de = moment(e.end).format('YYYY-MM-DD');
        dispatch(ManageActions.getmanagerdashboardRequest(companyId, ds, de));
        setDateStart(ds);
        setDateEnd(de);
    };

    const handleFilter = (type, e) => {
        if (type === 'company' && e.length > 0) {
            const _cId = e[0].id < 0 ? cId : e[0].id;
            dispatch(ManageActions.getmanagerdashboardRequest(_cId, dateStart, dateEnd));
            dispatch(
                AppActions.getemployeesearchRequest({
                    companyId: _cId,
                    order: 'hired_at',
                    sort: 'DESC',
                })
            );
            dispatch(CompanyActions.getbusinessRequest({ id: e[0].business_id }));
            setCompanyId(_cId);
        }
    };

    const handleCompanyProfile = () => {
        let _cId = selectedCompany[0];
        if (_cId < 0) _cId = cId;
        const company = find(propEq('id', _cId), companies);
        if (company) {
            const payload = { id: company.business_id };
            const route = company?.entity?.slug ? { pathname: `/${company.entity.slug}/about` } : { pathname: '/business/about', search: `?id=${company.business_id}` };
            dispatch(CompanyActions.getbusinessRequest(payload, route));
        }
    };

    const date = useMemo(() => {
        return moment.range(dateStart, dateEnd);
    }, [dateStart, dateEnd]);
    const company = useMemo(() => {
        return find(propEq('id', companyId), companies);
    }, [companyId, companies]);
    const newHire = useMemo(() => {
        return filter((e) => moment(e?.hired_at, 'YYYY-MM-DD').isBetween(dateStart, dateEnd, null, '[]'), employees);
    }, [employees, dateStart, dateEnd]);

    useEffect(() => {
        dispatch(ManageActions.getmanagerdashboardRequest(companyId > 0 ? companyId : cId, dateStart, dateEnd));
    }, []);

    const handleReportMenu = (e) => {
        switch (e) {
            case 'print':
                setShowPrint(true);
                break;
        }
    };

    return (
        <ErrorBoundary className="mng-dashboard">
            <Filter onChange={handleFilter} />
            {company && (
                <div className="card flex-row mb-3">
                    <Avatar type="logo" size="large" url={company?.data?.profile?.avatar || '/images/default_company.svg'} name={company?.name} borderWidth={2} borderColor="#e0e0e0" />
                    <div className="d-flex-1 ms-3 ms-md-5 pt-2">
                        <p className="dsl-b22 bold mb-1 mb-md-3">{company?.name}</p>
                        <p className="dsl-b14 text-400 mb-2">{company?.data?.profile?.address?.street}</p>
                        <p className="dsl-b14 text-400 mb-2 mb-md-2">{`${company?.data?.profile?.address?.city || ''} ${company.data?.profile?.address?.state || ''}`}</p>
                        <p className="dsl-b14 text-400 mobile-screen">Employees: {reports?.users?.length}</p>
                    </div>
                    <div className="top-stats">
                        <div className="d-flex">
                            <DatePicker calendar="range" append="caret" format="MMM D" as="span" align="right" value={date} onSelect={handleDate} />
                            <EditDropdown className="ml-3" dataCy="editDropdown" options={['Print']} onChange={handleReportMenu} />
                            {showPrint && <PdfIcon dateStart={dateStart} dateEnd={dateEnd} reports={reports} name="Print" />}
                        </div>
                        <p className="dsl-b14 text-400">Employees: {reports?.users?.length}</p>
                    </div>
                </div>
            )}
            <div className="mr-0 me-md-2 w-100">
                <div className="custom-wrap-block">
                    <TrainingActivity />
                    <NewHireOrientations employees={newHire} />
                </div>
                <div className="custom-wrap-block">
                    <PerformanceReviews />
                    <CareerManagement />
                </div>
                <div className="custom-wrap-block">
                    <TasksProjects />
                    <EmployeeRecords onCompany={handleCompanyProfile} />
                </div>
            </div>
        </ErrorBoundary>
    );
};

export default ManagerDashboard;
